import React, { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  onSnapshot, 
  serverTimestamp 
} from "firebase/firestore";
import { 
  Settings, 
  Ticket, 
  History, 
  Trophy, 
  RotateCw, 
  Plus, 
  Trash2, 
  LayoutDashboard,
  Gift,
  CheckCircle2,
  AlertTriangle,
  Lock, 
  LogOut,
  User,
  XCircle,
  Percent,
  ChevronRight,
  Target,
  Zap,
  Image as ImageIcon,
  ShieldCheck,
  Copyright,
  Camera,
  QrCode,
  Search,
  Calendar,
  Copy,
  Check
} from "lucide-react";

// Konfigurasi Firebase
const firebaseConfig = {
  apiKey: "AIzaSyAmyNmznlF3NJtuhZ0DFpHt68oBCvoAulg",
  authDomain: "spinwheel-cuan855.firebaseapp.com",
  projectId: "spinwheel-cuan855",
  storageBucket: "spinwheel-cuan855.firebasestorage.app",
  messagingSenderId: "627079997400",
  appId: "1:627079997400:web:a44aef6cd5c1cab64734ad"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "cuan855-production"; 

// Aset Visual
const LOGO_URL = "https://content-horizon.b-cdn.net/cuan855/logomobile.png";
const FAVICON_URL = "https://content-horizon.b-cdn.net/cuan855/favicon2.png";

// Palette Warna Merah Terang & Emas Menyala
const AUTO_COLORS = [
  "#dc2626", "#111111", "#d4af37", "#ef4444", 
  "#b8860b", "#1a0505", "#facc15", "#991b1b", 
  "#eab308", "#2b0404"
];

export default function App() {
  const [view, setView] = useState("user"); 
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [prizes, setPrizes] = useState([]);
  const [history, setHistory] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [appSettings, setAppSettings] = useState({ forcedPrizeId: null });
  const [loading, setLoading] = useState(true);

  // Set Favicon secara dinamis
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement("link");
    link.type = "image/png";
    link.rel = "shortcut icon";
    link.href = FAVICON_URL;
    document.getElementsByTagName("head")[0].appendChild(link);
    document.title = "CUAN855 - Spinwheel Raih Keberuntungan Kamu Sekarang";
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const initialToken = typeof window !== "undefined" ? window.__initial_auth_token : undefined;
        if (initialToken) {
          try {
            await signInWithCustomToken(auth, initialToken);
          } catch (tokenErr) {
            await signInAnonymously(auth);
          }
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Kesalahan Autentikasi:", err);
        setLoading(false);
      }
    };
    initAuth();
    
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setIsAdmin(u && !u.isAnonymous);
      if (u) setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const prizesRef = collection(db, "artifacts", appId, "public", "data", "prizes");
    const historyRef = collection(db, "artifacts", appId, "public", "data", "history");
    const settingsRef = doc(db, "artifacts", appId, "public", "data", "settings", "global");

    const unsubPrizes = onSnapshot(prizesRef, (snap) => {
      setPrizes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("Gagal memuat hadiah:", err));

    const unsubHistory = onSnapshot(historyRef, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHistory(data.sort((a, b) => (b.time || 0) - (a.time || 0)));
    }, (err) => console.error("Gagal memuat riwayat:", err));

    const unsubSettings = onSnapshot(settingsRef, (snap) => {
      if (snap.exists()) setAppSettings(snap.data());
    }, (err) => console.error("Gagal memuat pengaturan:", err));

    let unsubCoupons = () => {};
    if (isAdmin) {
      const couponsRef = collection(db, "artifacts", appId, "public", "data", "coupons");
      unsubCoupons = onSnapshot(couponsRef, (snap) => {
        setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => console.error("Gagal memuat kupon:", err));
    }

    return () => {
      unsubPrizes();
      unsubHistory();
      unsubCoupons();
      unsubSettings();
    };
  }, [user, isAdmin]);

  const handleAdminLogout = async () => {
    await signOut(auth);
    await signInAnonymously(auth);
    setView("user");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#150202] flex flex-col items-center justify-center p-6 text-center">
      <div className="animate-spin text-amber-400 mb-6"><RotateCw size={48} /></div>
      <p className="text-red-400 uppercase text-[10px] tracking-widest font-bold">Menghubungkan ke Server CUAN855</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2a0404] via-[#140202] to-black text-[#f59e0b] font-sans selection:bg-red-500/30 flex flex-col w-full overflow-x-hidden relative">
      {/* Navbar Merah Terang Gold */}
      <nav className="border-b border-red-600/50 bg-black/90 backdrop-blur-md sticky top-0 z-50 p-4 sm:p-5 w-full shadow-lg shadow-red-950/60">
        <div className="max-w-6xl mx-auto flex justify-center items-center w-full relative">
          {/* Logo Tengah */}
          <div className="flex items-center gap-2 cursor-pointer transition-opacity hover:opacity-80" onClick={() => setView("user")}>
            <img src={LOGO_URL} alt="CUAN855" className="h-8 sm:h-12 object-contain filter drop-shadow-[0_2px_10px_rgba(239,68,68,0.7)]" />
          </div>
          
          {/* Tombol Aksi di Kanan */}
          <div className="absolute right-0 flex items-center gap-2">
            {isAdmin && view === "admin" && (
              <button onClick={handleAdminLogout} className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border border-red-500/60 text-red-400 text-[10px] font-black uppercase tracking-widest hover:bg-red-900/40 transition-all shadow-[0_0_12px_rgba(239,68,68,0.3)]">
                <LogOut size={12} /> <span className="hidden xs:inline">Keluar</span>
              </button>
            )}
            {view === "admin" && (
              <button onClick={() => setView("user")} className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border border-amber-500 bg-red-950/50 text-amber-300 text-[10px] font-black uppercase tracking-widest hover:bg-red-900/60 transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                <LayoutDashboard size={12} /> <span className="hidden xs:inline">Lucky Draw</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Konten Utama */}
      <div className="flex-1 max-w-6xl mx-auto p-4 py-6 sm:py-10 w-full overflow-x-hidden">
        {view === "user" ? (
          <UserView prizes={prizes} appSettings={appSettings} />
        ) : (
          isAdmin ? <AdminView prizes={prizes} coupons={coupons} history={history} appSettings={appSettings} /> : <AdminLogin />
        )}
      </div>

      {/* Footer Merah Gold */}
      <footer className="mt-12 border-t border-red-600/50 bg-black/95 py-10 sm:py-16 px-6 w-full overflow-hidden">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <img src={LOGO_URL} alt="CUAN855" className="h-10 sm:h-14 opacity-90 mb-4 filter drop-shadow-[0_0_12px_rgba(239,68,68,0.5)]" />
            <p className="text-zinc-400 text-[9px] sm:text-[11px] uppercase tracking-[0.4em] font-bold max-w-sm leading-relaxed px-4">
              Sistem Lucky Draw Terpercaya Dengan Jaminan Keamanan Data Member.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 text-amber-400 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest">
            <span className="hover:text-red-400 transition-colors cursor-pointer">Bantuan</span>
            <span className="hover:text-red-400 transition-colors cursor-pointer">TOS</span>
            <button 
              onClick={() => setView(view === "admin" ? "user" : "admin")}
              className="text-red-800 hover:text-amber-500 transition-colors flex items-center gap-1 opacity-50 hover:opacity-100"
            >
              <ShieldCheck size={10} /> Admin Gate
            </button>
          </div>
          <div className="flex items-center gap-2 text-red-700/80 text-[9px] uppercase tracking-tighter text-center font-bold">
            <Copyright size={10} />
            <span>2024-2025 CUAN855 Entertainment Group. All Rights Reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError("Email atau Password pengelola salah.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-6 sm:mt-16 animate-in fade-in zoom-in duration-300 px-2">
      <div className="bg-gradient-to-b from-[#2b0505] to-[#0f0202] border-2 border-red-600/60 p-8 sm:p-10 rounded-[2.5rem] shadow-[0_0_40px_rgba(239,68,68,0.3)] text-center">
        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-600/20 rounded-2xl flex items-center justify-center mb-6 mx-auto border border-amber-500/40 shadow-[0_0_15px_rgba(239,68,68,0.4)]">
          <Lock className="text-amber-400" size={30} />
        </div>
        <h2 className="text-xl sm:text-2xl font-black text-amber-400 mb-2 tracking-tight uppercase italic drop-shadow-[0_2px_8px_rgba(220,38,38,0.8)]">Admin Portal</h2>
        <p className="text-zinc-400 text-[10px] uppercase mb-8 tracking-widest font-bold">Verifikasi Identitas Pengelola</p>
        <form onSubmit={handleLogin} className="space-y-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-black/90 border border-red-700/60 p-4 rounded-2xl text-white outline-none focus:border-amber-400 text-sm font-bold shadow-inner focus:ring-2 focus:ring-red-500/30" placeholder="Email Admin" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-black/90 border border-red-700/60 p-4 rounded-2xl text-white outline-none focus:border-amber-400 text-sm font-bold shadow-inner focus:ring-2 focus:ring-red-500/30" placeholder="Password" />
          {error && <div className="text-[10px] text-red-400 font-bold uppercase py-1">{error}</div>}
          <button type="submit" disabled={isProcessing} className="w-full bg-gradient-to-b from-amber-300 via-amber-500 to-red-600 text-black py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-red-950 active:scale-95 transition-all border border-amber-300/50 hover:brightness-110">
            {isProcessing ? "Verifikasi..." : "Login Admin"}
          </button>
        </form>
      </div>
    </div>
  );
}

function UserView({ prizes, appSettings }) {
  const [couponCode, setCouponCode] = useState("");
  const [isValidated, setIsValidated] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [winData, setWinData] = useState({ label: null, code: null });
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validateCoupon = async () => {
    if (!couponCode) return;
    try {
      const docRef = doc(db, "artifacts", appId, "public", "data", "coupons", couponCode.toUpperCase());
      const snap = await getDoc(docRef);
      if (snap.exists() && !snap.data().used) {
        setIsValidated(true);
        setMsg({ text: "Kupon Valid! Silakan Mainkan Roda.", type: "success" });
      } else {
        setMsg({ text: "Kupon Tidak Aktif atau Sudah Digunakan.", type: "error" });
      }
    } catch (e) {
      setMsg({ text: "Sistem sibuk, silakan coba lagi.", type: "error" });
    }
  };

  const startSpin = async () => {
    if (isSpinning || !isValidated || prizes.length === 0) return;
    setIsSpinning(true);

    let winnerIndex = 0;
    if (appSettings?.forcedPrizeId) {
      const idx = prizes.findIndex(p => p.id === appSettings.forcedPrizeId);
      winnerIndex = idx !== -1 ? idx : 0;
    } else {
      const totalWeight = prizes.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
      if (totalWeight > 0) {
        let random = Math.random() * totalWeight;
        for (let i = 0; i < prizes.length; i++) {
          random -= (Number(prizes[i].weight) || 0);
          if (random <= 0) {
            winnerIndex = i;
            break;
          }
        }
      }
    }

    const segmentAngle = 360 / prizes.length;
    const extraSpins = 12 + Math.floor(Math.random() * 5);
    const targetAngle = 360 - (winnerIndex * segmentAngle) - (segmentAngle / 2);
    const currentModulo = rotation % 360;
    const distance = (targetAngle - currentModulo + 360) % 360;
    const newRotation = rotation + (extraSpins * 360) + distance;
    
    setRotation(newRotation);

    setTimeout(async () => {
      const winner = prizes[winnerIndex];
      const uniqueWinId = Math.random().toString(36).substring(2, 10).toUpperCase();
      setWinData({ label: winner.label, code: uniqueWinId });
      setShowWin(true);
      try {
        await updateDoc(doc(db, "artifacts", appId, "public", "data", "coupons", couponCode.toUpperCase()), { used: true, usedAt: Date.now() });
        await addDoc(collection(db, "artifacts", appId, "public", "data", "history"), { 
          prize: winner.label, 
          coupon: couponCode.toUpperCase(), 
          time: Date.now(),
          winId: uniqueWinId 
        });
      } catch (e) { console.error("History Log Error:", e); }
      setIsSpinning(false);
    }, 5500);
  };

  const handleCopyCode = () => {
    if (!winData.code) return;
    const textArea = document.createElement("textarea");
    textArea.value = winData.code;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy Error:", err);
    }
    document.body.removeChild(textArea);
  };

  // Lampu Terang Merah-Emas
  const lights = Array.from({ length: 32 }).map((_, i) => (
    <div 
      key={i} 
      className={`absolute w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full animate-pulse ${
        i % 2 === 0 
          ? "bg-amber-300 shadow-[0_0_12px_#facc15]" 
          : "bg-red-500 shadow-[0_0_12px_#ef4444]"
      }`}
      style={{
        top: "50%",
        left: "50%",
        transform: `translate(-50%, -50%) rotate(${i * (360/32)}deg) translateY(${isMobile ? "-125px" : "-232px"})`,
        animationDelay: `${i * 0.1}s`,
        zIndex: 50
      }}
    />
  ));

  return (
    <div className="flex flex-col items-center gap-8 sm:gap-14 animate-in fade-in duration-700 px-2 w-full max-w-full overflow-x-hidden">
      <div className="text-center px-4 w-full">
        <h1 className="text-4xl xs:text-5xl sm:text-8xl font-black tracking-tighter mb-2 bg-gradient-to-b from-amber-200 via-amber-400 to-red-500 bg-clip-text text-transparent italic uppercase leading-none text-center drop-shadow-[0_6px_20px_rgba(239,68,68,0.6)]">
          Lucky Draw
        </h1>
        <p className="text-amber-400 uppercase text-[9px] sm:text-[11px] tracking-[0.4em] sm:tracking-[0.8em] font-black">
          Acara Eksklusif CUAN855
        </p>
      </div>

      {!isValidated ? (
        <div className="w-full max-w-sm px-4">
          <div className="flex gap-2">
            <input 
              value={couponCode} 
              onChange={(e) => setCouponCode(e.target.value)} 
              placeholder="MASUKKAN KODE" 
              className="flex-1 bg-[#1a0404] border-2 border-red-600/70 p-4 sm:p-5 rounded-2xl text-amber-300 font-black outline-none focus:border-amber-400 uppercase shadow-inner text-sm min-w-0 placeholder-red-700/60 focus:ring-2 focus:ring-red-500/40" 
            />
            <button 
              onClick={validateCoupon} 
              className="bg-gradient-to-b from-amber-300 via-amber-400 to-red-600 text-black px-6 sm:px-10 rounded-2xl font-black text-sm uppercase shadow-lg shadow-red-950 active:scale-95 transition-all shrink-0 border border-amber-300/60 hover:brightness-110"
            >
              CEK
            </button>
          </div>
          {msg.text && (
            <p className={`mt-3 text-[10px] text-center font-black uppercase tracking-widest ${msg.type === "error" ? "text-red-400" : "text-amber-400"}`}>
              {msg.text}
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-amber-300 bg-red-900/40 px-5 py-2.5 sm:px-8 sm:py-4 rounded-full border border-amber-400/50 animate-pulse mx-4 max-w-full shadow-[0_0_20px_rgba(239,68,68,0.3)]">
          <CheckCircle2 size={16} className="text-amber-400" /> 
          <span className="text-[10px] font-black uppercase tracking-widest truncate">KUPON AKTIF: {couponCode.toUpperCase()}</span>
        </div>
      )}

      {/* Roda Spin Terang Merah & Gold */}
      <div className="relative flex justify-center w-full max-w-full overflow-visible py-4">
        <div className="absolute inset-0 z-10 pointer-events-none">
          {lights}
        </div>
        
        {/* Jarum Penunjuk Emas-Merah Menyala */}
        <div className="absolute top-[-25px] sm:top-[-50px] left-1/2 -translate-x-1/2 w-10 sm:w-16 z-[100] filter drop-shadow-[0_10px_15px_rgba(239,68,68,0.8)]">
          <svg viewBox="0 0 100 100">
            <path d="M50 100 L20 20 Q50 0 80 20 Z" fill="#fef08a" stroke="#dc2626" strokeWidth="6"/>
          </svg>
        </div>

        <div 
          className="w-[245px] h-[245px] xs:w-[295px] xs:h-[295px] sm:w-[480px] sm:h-[480px] rounded-full border-[12px] sm:border-[22px] border-red-950 relative transition-all duration-[5500ms] cubic-bezier(0.15, 0, 0.15, 1) shadow-[0_0_90px_rgba(239,68,68,0.5)] ring-4 ring-amber-400/60 bg-[#2d0505] overflow-hidden" 
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {/* Latar Belakang Segmen Warna Merah Terang & Gold */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            {prizes.map((p, i) => {
              const angle = 360 / prizes.length;
              const segmentRotation = i * angle;
              const segmentBg = AUTO_COLORS[i % AUTO_COLORS.length];
              return (
                <div 
                  key={`bg-${p.id}`}
                  className="absolute w-1/2 h-1/2 origin-bottom-right" 
                  style={{ 
                    transform: `rotate(${segmentRotation}deg) skewY(${90 - angle}deg)`, 
                    backgroundColor: segmentBg, 
                    border: "1.5px solid rgba(251, 191, 36, 0.35)",
                    zIndex: 1
                  }}
                />
              );
            })}
          </div>
          
          {/* Teks Hadiah */}
          <div className="absolute inset-0 z-10 pointer-events-none">
            {prizes.map((p, i) => {
              const angle = 360 / prizes.length;
              const segmentRotation = i * angle;
              return (
                <div 
                  key={`icon-${p.id}`}
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ transform: `rotate(${segmentRotation + angle / 2}deg)` }}
                >
                  <div className="flex flex-col items-center justify-center" style={{ transform: `translateY(${isMobile ? "-70px" : "-175px"}) rotate(0deg)` }}>
                    <span 
                      className="font-black uppercase text-[10px] sm:text-[14px] text-white text-center leading-tight max-w-[55px] sm:max-w-[100px]"
                      style={{ 
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,1)) drop-shadow(0 0 10px rgba(0,0,0,0.9))"
                      }}
                    >
                      {p.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hub Tengah Emas */}
          <div className="absolute inset-0 m-auto w-10 h-10 sm:w-24 sm:h-24 bg-[#0d0202] rounded-full border-2 sm:border-4 border-amber-400 z-30 flex items-center justify-center shadow-[0_0_50px_rgba(0,0,0,0.9)] ring-2 ring-red-600">
            <div className="w-3 h-3 sm:w-8 sm:h-8 bg-amber-400 rounded-full animate-pulse shadow-[0_0_30px_#f59e0b]"></div>
          </div>
        </div>
      </div>

      {/* Tombol Putar Merah Gold Terang */}
      <button 
        disabled={!isValidated || isSpinning || prizes.length === 0} 
        onClick={startSpin} 
        className="w-full max-w-sm bg-gradient-to-b from-amber-200 via-amber-400 to-red-600 text-black py-5 sm:py-7 rounded-[2.5rem] text-xl sm:text-2xl font-black uppercase tracking-[0.3em] shadow-[0_0_35px_rgba(239,68,68,0.5)] disabled:opacity-20 active:scale-95 border-b-4 border-red-950 hover:brightness-110 transition-all mx-4 border-t border-amber-200"
      >
        {isSpinning ? "MEMUTAR..." : "PUTAR SEKARANG"}
      </button>

      {/* Modal Kemenangan Merah Gold */}
      {showWin && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/98 backdrop-blur-xl animate-in fade-in duration-500 overflow-y-auto w-full">
          <div className="bg-gradient-to-b from-[#3b0606] via-[#1a0202] to-[#0a0202] border-2 border-red-500/80 rounded-[3rem] p-8 md:p-12 max-w-sm w-full text-center relative shadow-[0_0_150px_rgba(239,68,68,0.6)] my-auto max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            <h2 className="text-4xl sm:text-5xl font-black text-amber-300 mt-4 mb-4 tracking-tighter italic uppercase drop-shadow-[0_2px_12px_rgba(239,68,68,0.9)]">
              JACKPOT
            </h2>
            
            <div className="bg-black/90 py-6 sm:py-10 rounded-[2rem] border-2 border-red-600/60 text-2xl sm:text-4xl font-black text-amber-400 uppercase mb-8 shadow-inner px-2">
              {String(winData.label)}
            </div>

            <div className="bg-red-950/40 p-6 rounded-[2.5rem] border border-amber-500/40 mb-8 flex flex-col items-center gap-5">
              <div className="flex items-center gap-2 text-amber-400 text-[10px] font-bold uppercase tracking-widest">
                <QrCode size={14} className="text-amber-400" /> KODE VERIFIKASI
              </div>
              <div className="p-3 bg-white rounded-2xl shadow-lg ring-4 ring-red-500/40">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${winData.code}`} alt="Verification QR" className="w-24 h-24 sm:w-32 sm:h-32" />
              </div>
              <div className="flex items-center gap-2 w-full">
                <div className="flex-1 bg-black/90 border border-red-800 p-3.5 rounded-xl font-mono text-amber-300 text-base font-black tracking-[0.2em] overflow-hidden truncate">
                  {winData.code}
                </div>
                <button onClick={handleCopyCode} className="bg-amber-400 hover:bg-amber-300 text-black p-3.5 rounded-xl transition-all active:scale-90 flex items-center justify-center shadow-lg border border-amber-200">
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              {copied && <span className="text-[10px] text-green-400 font-black uppercase animate-pulse">Kode Disalin!</span>}
            </div>

            <div className="flex items-center justify-center gap-3 p-5 bg-red-600/20 border border-red-500/30 rounded-3xl mb-8">
              <Camera size={20} className="text-amber-400 shrink-0" />
              <p className="text-left text-zinc-200 text-[10px] font-bold uppercase leading-relaxed">
                HARAP AMBIL <span className="text-amber-300">SCREENSHOT</span> HALAMAN INI UNTUK KLAIM HADIAH ANDA.
              </p>
            </div>

            <button onClick={() => window.location.reload()} className="w-full bg-gradient-to-b from-amber-300 to-red-600 text-black py-5 rounded-2xl font-black uppercase tracking-[0.2em] active:scale-95 transition-all shadow-xl text-sm border-b-4 border-red-950 hover:brightness-110">SELESAI</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminView({ prizes, coupons, history, appSettings }) {
  const [tab, setTab] = useState("prizes");
  const [newPrize, setNewPrize] = useState({ label: "", weight: 0, icon: "" });
  const [couponQty, setCouponQty] = useState(10);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchHistory, setSearchHistory] = useState("");
  
  const totalWeight = prizes.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);

  const addPrize = async () => {
    if (!newPrize.label || prizes.length >= 10) return;
    setIsProcessing(true);
    try {
      await addDoc(collection(db, "artifacts", appId, "public", "data", "prizes"), { 
        label: newPrize.label.toUpperCase(), 
        weight: Number(newPrize.weight), 
        icon: newPrize.icon,
        createdAt: Date.now() 
      });
      setNewPrize({ label: "", weight: 0, icon: "" });
    } finally { setIsProcessing(false); }
  };

  const setForcedWinner = async (id) => {
    try {
      const settingsRef = doc(db, "artifacts", appId, "public", "data", "settings", "global");
      await setDoc(settingsRef, { forcedPrizeId: id }, { merge: true });
    } catch (e) { console.error("Set Pemenang Gagal:", e); }
  };

  const generateCoupons = async () => {
    setIsProcessing(true);
    try {
      for (let i = 0; i < couponQty; i++) {
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();
        await setDoc(doc(db, "artifacts", appId, "public", "data", "coupons", code), { used: false, createdAt: Date.now() });
      }
    } finally { setIsProcessing(false); }
  };

  const filteredHistory = history.filter(h => 
    String(h.coupon).toLowerCase().includes(searchHistory.toLowerCase()) || 
    String(h.winId).toLowerCase().includes(searchHistory.toLowerCase()) ||
    String(h.prize).toLowerCase().includes(searchHistory.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-6 duration-500 overflow-x-hidden pb-10 px-1 w-full max-w-full">
      <div className="flex gap-4 border-b border-red-600/40 overflow-x-auto custom-scrollbar whitespace-nowrap pb-3 no-scrollbar">
        <button onClick={() => setTab("prizes")} className={`pb-2 px-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${tab === "prizes" ? "border-b-4 border-amber-400 text-amber-300" : "text-zinc-500"}`}><Gift size={14} /> Kelola Hadiah</button>
        <button onClick={() => setTab("coupons")} className={`pb-2 px-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${tab === "coupons" ? "border-b-4 border-amber-400 text-amber-300" : "text-zinc-500"}`}><Ticket size={14} /> Kupon</button>
        <button onClick={() => setTab("rig")} className={`pb-2 px-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${tab === "rig" ? "border-b-4 border-amber-400 text-amber-300" : "text-zinc-500"}`}><Target size={14} /> Pengaturan JP</button>
        <button onClick={() => setTab("history")} className={`pb-2 px-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${tab === "history" ? "border-b-4 border-amber-400 text-amber-300" : "text-zinc-500"}`}><History size={14} /> Riwayat JP</button>
      </div>

      {tab === "prizes" ? (
        <div className="grid lg:grid-cols-2 gap-8 w-full">
          <div className="bg-[#1c0404]/90 p-6 sm:p-10 rounded-[2.5rem] border border-red-600/50 shadow-2xl h-fit">
            <h3 className="text-amber-400 font-black text-xl mb-8 flex items-center gap-3">
              <Plus size={24} className="text-red-500" /> TAMBAH HADIAH
            </h3>
            <div className="space-y-5">
              <input value={newPrize.label} onChange={e => setNewPrize({...newPrize, label: e.target.value})} className="w-full bg-black/90 border border-red-700/60 p-4 rounded-2xl text-white font-bold outline-none focus:border-amber-400 text-sm shadow-inner" placeholder="NAMA HADIAH" />
              <input value={newPrize.icon} onChange={e => setNewPrize({...newPrize, icon: e.target.value})} className="w-full bg-black/90 border border-red-700/60 p-4 rounded-2xl text-white font-bold outline-none focus:border-amber-400 text-sm shadow-inner" placeholder="URL IKON (Opsional)" />
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <input type="number" value={newPrize.weight} onChange={e => setNewPrize({...newPrize, weight: e.target.value})} className="w-full bg-black/90 border border-red-700/60 p-4 rounded-2xl text-white font-bold outline-none focus:border-amber-400 text-sm shadow-inner" placeholder="PELUANG %" />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                </div>
                <div className="p-4 bg-black/60 rounded-2xl border border-red-900/40 text-[10px] text-zinc-500 flex items-center justify-center italic text-center">Warna Otomatis</div>
              </div>
              <button onClick={addPrize} disabled={prizes.length >= 10 || isProcessing} className="w-full bg-gradient-to-b from-amber-300 via-amber-400 to-red-600 text-black py-4 rounded-2xl font-black uppercase tracking-widest active:scale-95 transition-all shadow-xl disabled:opacity-30 text-xs sm:text-sm border border-amber-200/40">Simpan Hadiah</button>
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-zinc-400 font-black text-[10px] uppercase tracking-[0.3em] px-4">AKTIF ({prizes.length}/10)</h3>
            <div className="space-y-2.5">
              {prizes.map((p, idx) => (
                <div key={p.id} className="flex items-center justify-between bg-[#1f0404]/90 p-4 sm:p-5 rounded-[2rem] border border-red-700/40">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-contain bg-black p-1.5 border border-red-800 flex items-center justify-center shrink-0 text-amber-400">
                      <Gift size={24} />
                    </div>
                    <div className="min-w-0 pr-2">
                      <p className="text-white font-black text-xs sm:text-base tracking-tight truncate">{String(p.label)}</p>
                      <p className="text-[9px] sm:text-[10px] text-amber-400 font-black uppercase tracking-wider">{String(p.weight)}% Peluang</p>
                    </div>
                  </div>
                  <button onClick={() => deleteDoc(doc(db, "artifacts", appId, "public", "data", "prizes", p.id))} className="p-3 text-zinc-600 hover:text-red-400 transition-all shrink-0"><Trash2 size={20} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : tab === "rig" ? (
        <div className="max-w-2xl mx-auto space-y-8 text-center animate-in zoom-in duration-300 px-2 w-full">
          <Target className="text-amber-400 mx-auto mb-4" size={32} />
          <h3 className="text-xl sm:text-2xl font-black text-amber-400 mb-2 uppercase italic tracking-tighter">Setting Pemenang JP</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => setForcedWinner(null)} className={`p-5 rounded-[1.5rem] sm:rounded-[2rem] border-2 font-black uppercase transition-all shadow-xl text-xs sm:text-sm ${!appSettings?.forcedPrizeId ? "bg-gradient-to-b from-amber-300 to-red-600 border-amber-300 text-black shadow-red-950/50" : "bg-black/90 border-red-900/40 text-zinc-600"}`}>
              Mode Acak Normal
            </button>
            {prizes.map(p => (
              <button key={p.id} onClick={() => setForcedWinner(p.id)} className={`p-5 rounded-[1.5rem] sm:rounded-[2rem] border-2 font-black uppercase transition-all flex flex-col items-center gap-3 shadow-xl text-xs sm:text-sm ${appSettings?.forcedPrizeId === p.id ? "bg-red-900/80 border-amber-400 text-amber-300 shadow-red-900/60" : "bg-black/90 border-red-900/40 text-zinc-600"}`}>
                <span className="truncate w-full">{String(p.label)}</span>
              </button>
            ))}
          </div>
          {appSettings?.forcedPrizeId && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/40 rounded-2xl flex items-center justify-center gap-2 animate-pulse mt-4">
              <Zap size={14} className="text-amber-400" />
              <span className="text-[10px] font-black uppercase text-amber-400 tracking-widest">Pemenang Sudah Disetting Manual</span>
            </div>
          )}
        </div>
      ) : tab === "history" ? (
        <div className="space-y-6 animate-in fade-in duration-500 w-full">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-2 w-full">
            <h3 className="text-amber-400 font-black text-lg sm:text-xl flex items-center gap-2 uppercase tracking-tighter self-start"><History className="text-amber-400" /> Log Kemenangan</h3>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
              <input value={searchHistory} onChange={e => setSearchHistory(e.target.value)} placeholder="Cari Kupon / ID Verifikasi..." className="w-full bg-black/90 border border-red-800/60 pl-11 pr-4 py-3 rounded-2xl text-white text-[12px] outline-none shadow-inner" />
            </div>
          </div>
          <div className="bg-[#1c0404]/90 rounded-[2.5rem] border border-red-700/50 overflow-hidden shadow-2xl w-full">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead className="bg-black/90 text-[10px] uppercase text-amber-400 font-black tracking-[0.2em]">
                  <tr>
                    <th className="px-6 py-5 border-b border-red-800/50">Tanggal</th>
                    <th className="px-6 py-5 border-b border-red-800/50">Kupon</th>
                    <th className="px-6 py-5 border-b border-red-800/50">Hadiah</th>
                    <th className="px-6 py-5 border-b border-red-800/50">Win ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-900/30">
                  {filteredHistory.map((h, idx) => (
                    <tr key={idx} className="hover:bg-red-950/40 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-zinc-300 text-[11px] font-bold">{new Date(h.time).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
                          <span className="text-zinc-500 text-[9px]">{new Date(h.time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="bg-black/90 px-3 py-1.5 rounded-xl border border-red-700/60 text-amber-300 font-mono text-xs font-black tracking-widest">{h.coupon}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white font-black text-xs uppercase italic">{h.prize}</span>
                      </td>
                      <td className="px-6 py-4 font-mono text-zinc-500 text-[11px] group-hover:text-amber-300 transition-colors uppercase">{h.winId}</td>
                    </tr>
                  ))}
                  {filteredHistory.length === 0 && (
                    <tr><td colSpan={4} className="px-6 py-16 text-center text-zinc-600 italic text-sm uppercase tracking-widest">Data Tidak Ditemukan</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6 w-full">
          <div className="bg-[#1c0404]/90 p-8 rounded-[2.5rem] border border-red-700/50 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
            <div className="text-center sm:text-left flex items-center gap-6">
              <div className="p-5 bg-red-600/20 rounded-3xl border border-amber-400/30 text-amber-400 shadow-xl"><Ticket size={32} /></div>
              <div>
                <h3 className="text-amber-400 font-black text-xl mb-1 uppercase italic tracking-tighter">Generate Kupon</h3>
                <p className="text-zinc-400 text-[10px] uppercase tracking-widest font-bold">Buat Kode Baru Untuk Member</p>
              </div>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <input type="number" value={couponQty} onChange={e => setCouponQty(e.target.value)} className="w-full sm:w-28 bg-black/90 border border-red-700/60 p-4 rounded-2xl text-amber-300 text-center font-black outline-none focus:border-amber-400 text-base min-w-0" />
              <button onClick={generateCoupons} disabled={isProcessing} className="bg-gradient-to-b from-amber-300 to-red-600 text-black px-8 py-4 rounded-2xl font-black uppercase text-xs sm:text-sm tracking-widest shadow-xl active:scale-95 transition-all shrink-0">Buat</button>
            </div>
          </div>
          <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4 text-center w-full px-2">
            {coupons.sort((a,b) => (b.createdAt || 0) - (a.createdAt || 0)).map(c => (
              <div key={c.id} className={`p-4 rounded-2xl border transition-all shadow-inner ${c.used ? "bg-black/80 border-zinc-900 text-zinc-700 grayscale" : "bg-black/90 border-red-700/60 text-amber-400 hover:border-amber-400 shadow-red-950/30"}`}>
                <p className="font-mono font-black text-xs sm:text-sm tracking-tighter">{String(c.id)}</p>
                <p className="text-[7px] uppercase font-bold mt-1 opacity-50">{c.used ? "Terpakai" : "Aktif"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getContrastYIQ(hexcolor) {
  if (!hexcolor) return "white";
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? "black" : "white";
}